import { Typography } from "@mui/material";
import { ChatRoleBadge } from "Components/Chat/ChatRoleBadge";
import { ChatSelectionMarker } from "Components/Chat/ChatSelectionMarker";
import { SocialSummaryRow } from "Components/Social/SocialSummaryRow";
import { useSelectedUserIneligibility } from "Hooks/Chat/useSelectedUserIneligibility";
import type { SocialUserSearchDto } from "Infrastructure/Api/Api";
import { isAccessRoleCode } from "Models/AccessRoleCodes";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Chat.Members;

/** The caption of an inert row, by the reason it is inert. */
const INERT_REASON_RESOURCES = {
  alreadyMember: RESOURCES.AlreadyMember,
  customerWritesFirst: RESOURCES.CustomerWritesFirst,
  notFullMoone: RESOURCES.NotFullMoone,
  noHandle: RESOURCES.NoHandle,
} as const;

/** Matches the avatar SocialUserRow renders, so the role badge scales with it. */
const ROW_AVATAR_SIZE = 40;

type Props = {
  user: SocialUserSearchDto;
  /** Picked in this dialog — marked, and clicking again removes them. */
  isSelected: boolean;
  /** Already in the conversation — marked as such and inert; a distinct state, not a selection. */
  isExistingMember: boolean;
  /** The conversation admits Full Moone personas only (App ADR-0012); others are inert with a reason. */
  isFullMooneRequired?: boolean;
  /** Picking accumulates a selection, so unpicked rows show the empty circle. */
  isMultiSelect?: boolean;
  /** Toggle: adds the person when they are not selected, removes them when they are. */
  onSelect: (user: SocialUserSearchDto) => void;
};

/**
 * One person in the chat picker: the shared SocialSummaryRow with the role badge, plus the state
 * on the right — a selection marker on a pickable row, a caption on an inert one. Everything on
 * the right is decoration inside the row's own trigger, never a second control: one row, one
 * thing to click, whichever half you aim at.
 */
export const ChatUserSearchResultRow = ({
  user,
  isSelected,
  isExistingMember,
  isFullMooneRequired,
  isMultiSelect,
  onSelect,
}: Props) => {
  const { t } = useResource();
  const getIneligibility = useSelectedUserIneligibility(isFullMooneRequired);

  // An existing member and an ineligible person are both inert rows with a reason; being in the
  // conversation already is the more specific of the two.
  const inertReason = isExistingMember
    ? "alreadyMember"
    : getIneligibility(user);
  const isInert = inertReason !== undefined;
  // The mark and the row's pressed state travel together: a row only announces itself as a
  // toggle where it actually shows one.
  const isToggle = !isInert && (isMultiSelect || isSelected);

  const renderMarker = () => {
    if (inertReason) {
      return (
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          flexShrink={0}
        >
          {t(INERT_REASON_RESOURCES[inertReason])}
        </Typography>
      );
    }

    if (isToggle) {
      return <ChatSelectionMarker isSelected={isSelected} />;
    }

    return undefined;
  };

  return (
    <SocialSummaryRow
      user={user}
      // An inert row is disabled rather than simply un-clickable: without onSelect the
      // row falls back to navigating to the profile, which would abandon the dialog.
      isDisabled={isInert}
      onSelect={isInert ? undefined : () => onSelect(user)}
      isSelected={isToggle ? isSelected : undefined}
      marker={renderMarker()}
      // The chat picker shows the role badge, not the storefront one.
      avatarBadge={
        <ChatRoleBadge
          accessRole={
            isAccessRoleCode(user.accessRoleCode)
              ? user.accessRoleCode
              : undefined
          }
          avatarSize={ROW_AVATAR_SIZE}
        />
      }
    />
  );
};
